import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const WHATSAPP_NUMBER = "491798067007";

// WhatsApp SVG Logo
const WhatsAppLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const navItems = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Über uns', href: '#ueber-uns' },
  { label: 'So funktioniert\'s', href: '#prozess' },
  { label: 'Bewertungen', href: '#bewertungen' },
  { label: 'Standort', href: '#standort' },
  { label: 'FAQ', href: '#faq' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        data-testid="header"
        className="fixed top-0 left-0 right-0 z-50 bg-tiktok-black border-b border-white/10"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20 md:h-24 lg:h-28">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center">
              <span className="text-xl sm:text-2xl md:text-3xl font-black tracking-tighter whitespace-nowrap">
                <span className="text-white">PKW </span>
                <span className="text-tiktok-pink neon-text-pink">Ankauf</span>
                <span className="text-tiktok-cyan neon-text-cyan"> 24/7</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Desktop CTA Buttons - WhatsApp links, Anrufen rechts */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-whatsapp-btn"
              >
                <Button
                  className="bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold"
                >
                  <WhatsAppLogo className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a
                href={`tel:+${WHATSAPP_NUMBER}`}
                data-testid="header-phone-btn"
                className="flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-tiktok-cyan transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>Jetzt anrufen</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 text-white"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-[#050505] pt-20 lg:hidden"
          >
            <nav className="flex flex-col p-6 space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="text-lg font-medium text-gray-300 hover:text-white py-3 border-b border-white/10 text-left"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 space-y-3">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] rounded-xl text-white font-semibold"
                >
                  <WhatsAppLogo className="w-5 h-5" />
                  WhatsApp schreiben
                </a>
                <a
                  href={`tel:+${WHATSAPP_NUMBER}`}
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-tiktok-surface rounded-xl text-white"
                >
                  <Phone className="w-5 h-5" />
                  Jetzt anrufen
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
